import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { type Container } from "@/builder/types";
import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminReviewForm } from "../types";

export type ReviewFormList = Paginate<AdminReviewForm>;

export const reviewForms = createQueryKeys("admin-performance-review-forms", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "";

export const fetchReviewFormsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...reviewForms.all(page, search),
    queryFn: () =>
      adminApi
        .get<ReviewFormList>(`/performance/review-forms`, {
          params: {
            include,
            page,
            ...search,
          },
        })
        .then((response) => response.data),
  });

export const useFetchReviewForms = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchReviewFormsQueryOptions(page, search));

export const fetchReviewFormQueryOptions = (id: number | undefined) =>
  queryOptions({
    ...reviewForms.one(id),
    queryFn: () =>
      adminApi
        .get<
          AdminReviewForm<true>
        >(`/performance/review-forms/${id}?include=${include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReviewForm = (id: number | undefined) =>
  useQuery(fetchReviewFormQueryOptions(id));

export interface ReviewFormRequest {
  name: string;
  containers: Container[];
}

export const useCreateReviewForm = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ReviewFormRequest) =>
      adminApi
        .post<
          AdminReviewForm<true>
        >(`/performance/review-forms?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewForms.all._def,
      });
    },
  });
};

export const useUpdateReviewForm = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ReviewFormRequest) =>
      adminApi
        .put<
          AdminReviewForm<true>
        >(`/performance/review-forms/${id}?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewForms.all._def,
      });
      queryClient.invalidateQueries(reviewForms.one(id));
    },
  });
};

export const useCreateOrUpdateReviewForm = (id: number | undefined) => {
  const createReviewForm = useCreateReviewForm();
  const updateReviewForm = useUpdateReviewForm(id!);
  return id ? updateReviewForm : createReviewForm;
};

export const useDeleteReviewForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      adminApi.delete(`/performance/review-forms/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: reviewForms.all._def,
      });
      queryClient.invalidateQueries(reviewForms.one(id));
    },
  });
};
